// ────────────────────────────────────────────────────────────────────
// One destination for files, three lenses on the same pile.
//
// There used to be three places: the import console, the file room, and
// /documents. All three read the same intake_files rows, and all three were
// named as if they held different things — นำเข้าข้อมูล / ห้องไฟล์ /
// แพ็กเกจเอกสาร. The console even rendered a "ห้องไฟล์ทั้งหมด →" button INSIDE
// itself while the nav listed แพ็กเกจเอกสาร as its sibling, so the shape on
// screen contradicted the shape in the nav.
//
// They were never three places. They are three questions about one pile:
//
//   รอจัดการ    what has arrived and not been dealt with     (empties)
//   ตาม asset   everything, by where it is installed         (never empties)
//   ตามแพ็กเกจ   everything, by document number and revision  (never empties)
//
// The asset tree and the package tree are genuinely orthogonal — one file
// carries both asset_id and package_id, so a drawing lives under V-201 AND
// inside package 1200 at the same time (see DocumentsScreen's docstring). That
// justified two trees. It never justified two destinations.
//
// Rendered BY each screen rather than wrapping them: IntakeScreen and
// DocumentsScreen are both `absolute inset-0`, and nesting either under a tab
// bar would have meant rewriting layouts that work. Each screen draws this
// strip at the top of its own chrome and the route decides which lens is
// current, so the tabs are real links — bookmarkable, shareable, and
// back-button honest.
// ────────────────────────────────────────────────────────────────────
import { Link } from "react-router-dom";
import { Inbox, Boxes, Archive } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type FileView = "queue" | "assets" | "packages";

export const FILE_VIEWS: { key: FileView; label: string; icon: LucideIcon }[] = [
  { key: "queue",    label: "รอจัดการ",  icon: Inbox },
  { key: "assets",   label: "ตาม asset", icon: Boxes },
  { key: "packages", label: "ตามแพ็กเกจ", icon: Archive },
];

export function FileTabs({ active, pendingCount = 0 }: {
  active: FileView;
  /** Shown only on the queue, and only when non-zero: this is the one lens
   *  that can be finished, and a "0" badge reads as a problem rather than as
   *  the finish line it actually is. */
  pendingCount?: number;
}): JSX.Element {
  return (
    <div className="px-6 flex items-center gap-1 border-b shrink-0" style={{ borderColor: "var(--hairline)" }}>
      {FILE_VIEWS.map((v) => {
        const on = v.key === active;
        const Icon = v.icon;
        return (
          <Link key={v.key} to={`/files/${v.key}`} data-testid={`file-tab-${v.key}`}
                aria-current={on ? "page" : undefined}
                className="font-mono inline-flex items-center gap-1.5 px-3 py-2.5 transition-colors"
                style={{
                  fontSize: 11.5,
                  color: on ? "var(--accent-deep)" : "var(--ink-3)",
                  fontWeight: on ? 700 : 400,
                  borderBottom: on ? "2px solid var(--accent)" : "2px solid transparent",
                }}>
            <Icon size={13} />
            {v.label}
            {v.key === "queue" && pendingCount > 0 && (
              <span className="font-mono px-1.5" style={{
                fontSize: 9.5, color: "var(--ink-1)", background: "var(--accent)", fontWeight: 700,
              }}>{pendingCount}</span>
            )}
          </Link>
        );
      })}
    </div>
  );
}
